/**
 * Job Hunter — every call to /slighter_crm/job-hunter (backend/src/modules/slighter-crm/job-hunter).
 * Errors come back as `{ code, message }` and the shared axios client toasts them.
 */
import { api } from '@/config/api';

export type JhStatus = 'proposed' | 'saved' | 'approved' | 'needs_input' | 'ready' | 'applied' | 'interview' | 'offer' | 'rejected' | 'skipped' | 'withdrawn' | 'expired';
export type JhSignal = 'explicit' | 'likely' | 'none' | 'excluded';
export type JhHeat = 'hot' | 'warm' | 'cold' | 'dead';
export type JhOutcome = 'rejected' | 'queued' | 'below_threshold' | 'proposed' | 'error';
export type JhSourceType = 'greenhouse' | 'lever' | 'ashby' | 'workable' | 'smartrecruiters' | 'recruitee' | 'arbeitnow' | 'remotive' | 'himalayas' | 'adzuna';

export interface JhEvaluation {
  fitScore: number;
  sponsorshipSignal: JhSignal;
  sponsorshipQuote?: string;
  seniority?: string;
  languageRequirement?: string;
  country?: string;
  remoteType?: string;
  fitReasons: string[];
  redFlags: string[];
  salaryHint?: string;
  isAgency?: boolean;
  summary?: string;
  /** Work-visa route for the job's country, e.g. "Skilled Worker visa". */
  visaRoute?: string;
  /** "Monzo Bank Limited is on the UK Home Office register of licensed sponsors · Skilled Worker". */
  registerMatch?: string;
  tokensIn?: number;
  tokensOut?: number;
  credits?: number;
  at: string;
}

export interface JhDraft {
  coverNote: string;
  cvHighlights: string[];
  answers: Array<{ question: string; answer: string; source: 'truth_file' | 'user' }>;
  pendingQuestions: string[];
  /** Cover-note claims the backend could not find in the CV / truth file / portfolio. */
  unverifiedClaims?: string[];
  /** Claims the fact-check pass removed from the first draft (not supported by the CV / truth file / portfolio). */
  removedClaims?: string[];
  at: string;
}

export interface JhScreen {
  agentUid: string;
  agentName?: string;
  agentColor?: string;
  profileUid: string;
  profileName?: string;
  outcome: JhOutcome;
  reason?: string;
  reasonLabel?: string;
  score?: number;
  signal?: JhSignal;
  at: string;
}

export interface JhJob {
  jobUid: string;
  title: string;
  employerName: string;
  employerUid?: string;
  url: string;
  applyUrl?: string;
  country?: string;
  city?: string;
  locationRaw?: string;
  remoteType: 'onsite' | 'hybrid' | 'remote' | 'unknown';
  employmentType?: string;
  department?: string;
  salaryText?: string;
  postedAt?: string;
  firstSeenAt: string;
  lastSeenAt?: string;
  isOpen: boolean;
  closedAt?: string;
  openCheckedAt?: string;
  fromAts: boolean;
  sourceType: string;
  flags: string[];
  sponsorshipHits: string[];
  descriptionText?: string;
  jdLanguage?: string;
  seenOn?: Array<{ sourceUid: string; sourceType: string; url: string; at: string }>;
  screen?: JhScreen | null;
  screens?: JhScreen[];
}

export interface JhEvidence { type: 'posting' | 'register' | 'manual'; ref?: string; quote?: string; signal?: string; at: string }

export interface JhEmployer {
  employerUid: string;
  name: string;
  domain?: string;
  country?: string;
  careersUrl?: string;
  atsType?: string;
  atsSlug?: string;
  atsCheckedAt?: string;
  sponsorshipEvidence: JhEvidence[];
  registers?: Array<{ country: string; name: string; route?: string; approvals?: number; match: 'exact' | 'prefix' }>;
  status: 'watched' | 'seen' | 'paused' | 'blocked';
  isAgency: boolean;
  noSponsorshipFeedback: number;
  jobCount: number;
  proposedCount: number;
  openJobs?: number;
  lastJobAt?: string;
  notes?: string;
}

export interface JhHistory { status: string; at: string; by: string; note?: string }

export interface JhApplication {
  applicationUid: string;
  ref: number;
  profileUid: string;
  /** The agent that found it and the CV it was scored / drafted with. */
  agentUid?: string;
  cvUid?: string;
  jobUid: string;
  employerUid?: string;
  status: JhStatus;
  evaluation: JhEvaluation;
  draft?: JhDraft;
  decisionChannel?: string;
  decidedAt?: string;
  appliedAt?: string;
  externalRef?: string;
  notes?: string;
  jobClosed: boolean;
  duplicateWarning?: string;
  notifiedAt?: string;
  notifiedVia?: string;
  feedback?: { reason: string; note?: string; at: string };
  history: JhHistory[];
  createdAt: string;
  updatedAt: string;
  job: JhJob | null;
  employer?: JhEmployer | null;
  /** Detail only: label of the CV this application was scored / drafted with. */
  cvLabel?: string | null;
}

export interface JhSource {
  sourceUid: string;
  type: JhSourceType;
  name: string;
  key: string;
  label: string;
  tier: 1 | 2 | 3;
  employerUid?: string;
  employer: { employerUid: string; name: string; status: string; country?: string; evidence: number } | null;
  config: Record<string, unknown> & { hasAppKey?: boolean };
  enabled: boolean;
  heat: JhHeat;
  pollIntervalMin?: number;
  nextPollAt: string;
  lastPolledAt?: string;
  lastSuccessAt?: string;
  lastHitAt?: string;
  consecutiveFailures: number;
  lastError?: string;
  lastJobCount: number;
  lastNewCount: number;
  totalJobs: number;
  totalProposed: number;
}

export interface JhCatalogEntry { type: JhSourceType; label: string; tier: 1 | 2 | 3; description: string; ats: boolean }

export interface JhTruthFile {
  identity?: { name?: string; email?: string; phone?: string; location?: string };
  work_rights?: { nationality?: string; needs_sponsorship?: boolean; eu_work_permit?: boolean; current_visa_valid_until?: string };
  availability?: { notice_period_weeks?: number; earliest_relocation?: string; remote_trial_ok?: boolean };
  compensation?: { currency?: string; min_annual?: number; target_annual?: number };
  languages?: Array<{ code: string; level: string }>;
  education?: Array<{ degree: string; school: string; years: string }>;
  experience_years?: Record<string, number>;
  skills?: string[];
  links?: { portfolio?: string; github?: string; linkedin?: string };
  saved_answers?: Array<{ question: string; answer: string }>;
  never_answer?: string[];
  [key: string]: unknown;
}

export interface JhPortfolioItem {
  itemId: string;
  kind: 'android' | 'ios' | 'web';
  url: string;
  title: string;
  description?: string;
  iconUrl?: string;
  developer?: string;
  rating?: number;
  ratingCount?: number;
  installs?: string;
  category?: string;
  screenshots?: string[];
  techHints?: string[];
  role?: string;
  tech?: string[];
  fetchedAt: string;
  fetchError?: string;
}

export interface JhCvFile { name: string; mime: string; size: number; url: string; fileId: string; pages?: number }

export interface JhCvIssue { severity: 'high' | 'medium' | 'low'; area: string; problem: string; fix: string }
export interface JhCvCheck { id: string; label: string; ok: boolean; detail?: string }
export interface JhMarketDemand { term: string; jobs: number; inCv: boolean }
export interface JhCvHealth {
  score: number;
  verdict: string;
  /** 0–100 per area: ats, impact, clarity, relevance, keywords (empty when only the free checks ran). */
  categories: Record<string, number>;
  strengths: string[];
  issues: JhCvIssue[];
  missingKeywords: string[];
  rewrites: Array<{ before: string; after: string; why?: string }>;
  countryTips: string[];
  checks: JhCvCheck[];
  targetRoles: string[];
  targetCountries: string[];
  provider?: string;
  model?: string;
  credits?: number;
  at: string;
  /** Returned by analyze only. */
  demand?: JhMarketDemand[];
}

/** One CV in a candidate's library. The list endpoint leaves `text` out and trims `health` to score/verdict/at. */
export interface JhCv {
  cvUid: string;
  profileUid: string;
  label: string;
  isDefault: boolean;
  file?: JhCvFile | null;
  text?: string;
  textSource: 'file' | 'pasted' | 'edited';
  /** List rows: length of the text the agents read (0 for a file = not ATS-readable). */
  textChars?: number;
  health?: JhCvHealth | Pick<JhCvHealth, 'score' | 'verdict' | 'at'> | null;
  usedBy?: Array<{ agentUid: string; name: string; color: string; active: boolean }>;
  /** GET /cvs/:uid only: skills the matched jobs ask for, and whether this CV has them. */
  demand?: JhMarketDemand[];
  createdAt: string;
  updatedAt: string;
}

/** A search mission of one candidate: its own CV, roles, countries and bar. */
export interface JhAgent {
  agentUid: string;
  profileUid: string;
  name: string;
  color: string;
  active: boolean;
  cvUid?: string;
  roleKeywords: string[];
  searchQueries: string[];
  excludeTitleKeywords: string[];
  sponsorshipPositive: string[];
  sponsorshipNegative: string[];
  targetCountries: string[];
  excludedCountries: string[];
  acceptRemote: boolean;
  jdLanguages: string[];
  requireSponsorship: boolean;
  maxAgeDays: number;
  minScore: number;
  dailyScoreLimit: number;
  lastRunAt?: string;
  lastRun?: { screened: number; rejected: number; scored: number; proposed: number; queued: number; at: string } | null;
  createdAt?: string;
  /** List rows only. */
  cv?: { cvUid: string; label: string; isDefault: boolean; healthScore: number | null } | null;
  stats?: { matches: number; open: number; applied: number; queued: number; scoredToday: number };
}
export type JhAgentInput = Partial<Omit<JhAgent, 'agentUid' | 'cv' | 'stats' | 'lastRun' | 'lastRunAt'>>;

/** What reading a CV changed in the candidate's profile. */
export interface JhCvSync { changed: string[]; mode: 'fill' | 'replace'; aiUsed: boolean; error?: string }

/** A candidate (person): truth file, portfolio, WhatsApp alerts. CVs and agents hang off it. */
export interface JhProfile {
  profileUid: string;
  name: string;
  headline?: string;
  photo?: { url: string; fileId: string; uploadedAt: string } | null;
  /** The last CV read into this profile — shown until dismissed so the user checks it. */
  lastCvSync?: (JhCvSync & { cvUid: string; label: string; at: string }) | null;
  active: boolean;
  truthFile: JhTruthFile;
  portfolio: JhPortfolioItem[];
  instantThreshold: number;
  monthlyAiCallLimit: number;
  dailyApplyCap: number;
  decisionTimeoutHours: number;
  aiProvider?: string;
  aiModel?: string;
  notifyEnabled: boolean;
  notifyPhone?: string;
  notifyAccountUid?: string;
  /** Approved template for one match (highlights + Apply / Skip / Details) — used outside the 24 h window. */
  alertTemplateUid?: string;
  /** Approved digest template ("N new matches") — used outside the 24 h window. */
  wakeTemplateUid?: string;
  timezone: string;
  digestHour: number;
  quietFromHour: number;
  quietToHour: number;
  consumeAllFromPhone: boolean;
  waLastInboundAt?: string;
  lastNotifyError?: string;
  lastNotifyErrorAt?: string;
  usage?: { month?: string; aiCalls?: number; credits?: number };
  budget?: { monthLeft: number; monthCalls: number };
  windowOpen?: boolean;
  createdAt?: string;
  /** List rows only. */
  counts?: { agents: number; activeAgents: number; cvs: number; bestCvScore: number | null; openMatches: number; applied: number };
}

export interface JhTemplateState { templateUid: string; name: string; status: string; category: string; rejectionReason: string | null; linked: boolean }
export interface JhTemplateDefinition { name: string; language: string; category: string; headerType?: string; headerText?: string; body: string; footer?: string; buttons?: Array<{ type: string; text: string }>; previewSamples?: Record<string, string> }
export interface JhTemplateStatus {
  account: { accountUid: string; phone: string; name: string } | null;
  alert: JhTemplateState | null;
  digest: JhTemplateState | null;
  definitions: { alert: JhTemplateDefinition; digest: JhTemplateDefinition };
}

export interface JhRegisterStatus {
  importing: boolean;
  registers: Array<{ country: string; name: string; route: string; page: string; rows: number; importedAt: string | null; lastError: string | null; lastAttemptAt: string | null }>;
}

export interface JhRegion { key: string; label: string; codes: string[] }
export interface JhVisaRoute { route: string; note: string; register?: string }

export interface JhRun {
  runUid: string;
  trigger: 'schedule' | 'manual';
  agentUids?: string[];
  startedAt: string;
  finishedAt?: string;
  sourcesPolled: number;
  jobsSeen: number;
  jobsNew: number;
  jobsClosed: number;
  screened: number;
  rejected: number;
  scored: number;
  proposed: number;
  notified: number;
  aiCalls: number;
  credits: number;
  errorLog: string[];
}

export interface JhOverview {
  profile: Pick<JhProfile, 'profileUid' | 'name' | 'active' | 'notifyEnabled' | 'notifyPhone' | 'waLastInboundAt' | 'lastNotifyError' | 'lastNotifyErrorAt' | 'instantThreshold'>;
  agents: JhAgent[];
  kpis: { matchesThisWeek: number; awaitingDecision: number; saved: number; inProgress: number; applied: number; interviews: number; offers: number; avgFit: number | null };
  funnel: { discovered: number; screened: number; passedFilter: number; proposed: number; applied: number; interviews: number };
  counts: Partial<Record<JhStatus, number>>;
  signals: Partial<Record<JhSignal, number>>;
  countries: Array<{ country: string; n: number }>;
  daily: Array<{ day: string; proposed: number; applied: number }>;
  topMatches: JhApplication[];
  recent: JhApplication[];
  runs: JhRun[];
  jobs: { total: number; open: number; last24h: number; fromAts: number };
  sources: Partial<Record<JhHeat, number>>;
  employersWatched: number;
  ai: { monthCalls: number; monthLimit: number; dayScored: number; dayLimit: number; credits: number; tokensIn: number; tokensOut: number };
  queued: number;
}

export interface JhMeta {
  catalog: JhCatalogEntry[];
  statuses: JhStatus[];
  feedbackReasons: string[];
  filterReasons: Record<string, string>;
  countries: Array<{ code: string; name: string; eu: boolean }>;
  defaults: Partial<JhProfile> & { agent: JhAgentInput; regions: JhRegion[]; visaRoutes: Record<string, JhVisaRoute>; agentColors: string[] };
  /** Curated employers per region for "Load starter set". */
  seedRegions: Array<{ key: string; label: string; employers: number }>;
}

export interface JhPage<T> { rows: T[]; total: number; page: number; limit: number }
export interface JhNotifyOptions {
  accounts: Array<{ accountUid: string; phone: string; name: string; isActive: boolean; isDefault: boolean }>;
  templates: Array<{ templateUid: string; name: string; language: string; category: string; body: string; accountUid?: string; status: 'APPROVED' | 'PENDING' | string }>;
}

const BASE = '/slighter_crm/job-hunter';
const LONG = { timeout: 120_000 };
const id = encodeURIComponent;
const d = <T,>(p: Promise<{ data: T }>) => p.then((r) => r.data);

export const jobHunterService = {
  meta: () => d(api.get<JhMeta>(`${BASE}/meta`)),

  profiles: () => d(api.get<JhProfile[]>(`${BASE}/profiles`)),
  profile: (uid: string) => d(api.get<JhProfile>(`${BASE}/profiles/${id(uid)}`)),
  createProfile: (body: Partial<JhProfile>) => d(api.post<JhProfile>(`${BASE}/profiles`, body)),
  updateProfile: (uid: string, body: Partial<JhProfile>) => d(api.patch<JhProfile>(`${BASE}/profiles/${id(uid)}`, body)),
  deleteProfile: (uid: string) => d(api.delete<{ ok: boolean }>(`${BASE}/profiles/${id(uid)}`)),
  testNotify: (uid: string) => d(api.post<{ ok: boolean; error?: string }>(`${BASE}/profiles/${id(uid)}/test-notify`, {})),
  notifyOptions: () => d(api.get<JhNotifyOptions>(`${BASE}/notify-options`)),
  addPortfolio: (uid: string, body: { url: string; role?: string; tech?: string[] }) => d(api.post<JhPortfolioItem[]>(`${BASE}/profiles/${id(uid)}/portfolio`, body, LONG)),
  updatePortfolio: (uid: string, itemId: string, body: { role?: string; tech?: string[] }) => d(api.patch<JhPortfolioItem[]>(`${BASE}/profiles/${id(uid)}/portfolio/${id(itemId)}`, body)),
  refreshPortfolio: (uid: string, itemId: string) => d(api.post<JhPortfolioItem[]>(`${BASE}/profiles/${id(uid)}/portfolio/${id(itemId)}/refresh`, {}, LONG)),
  removePortfolio: (uid: string, itemId: string) => d(api.delete<JhPortfolioItem[]>(`${BASE}/profiles/${id(uid)}/portfolio/${id(itemId)}`)),
  templates: (uid: string) => d(api.get<JhTemplateStatus>(`${BASE}/profiles/${id(uid)}/templates`)),
  createTemplates: (uid: string, accountUid?: string) =>
    d(api.post<{ results: Record<string, string>; status: JhTemplateStatus }>(`${BASE}/profiles/${id(uid)}/templates`, { accountUid }, LONG)),
  syncTemplates: (uid: string) => d(api.post<JhTemplateStatus>(`${BASE}/profiles/${id(uid)}/templates/sync`, {}, LONG)),

  // Agents
  agents: (profileUid?: string) => d(api.get<JhAgent[]>(`${BASE}/agents`, { params: { profileUid } })),
  createAgent: (body: JhAgentInput & { profileUid: string }) => d(api.post<JhAgent>(`${BASE}/agents`, body)),
  updateAgent: (uid: string, body: JhAgentInput) => d(api.patch<JhAgent>(`${BASE}/agents/${id(uid)}`, body)),
  duplicateAgent: (uid: string) => d(api.post<JhAgent>(`${BASE}/agents/${id(uid)}/duplicate`, {})),
  deleteAgent: (uid: string) => d(api.delete<{ ok: boolean }>(`${BASE}/agents/${id(uid)}`)),

  // CV library
  cvs: (profileUid: string) => d(api.get<JhCv[]>(`${BASE}/profiles/${id(profileUid)}/cvs`)),
  /** New CV from a file; with `cvUid` the file of that CV is replaced (its agents keep using it). */
  /**
   * `syncProfile`: how the CV updates the candidate's profile — `fill` gaps, `replace` what the CV states,
   * `none`. Left out, the server fills gaps for a new CV and replaces when the default CV's file is replaced.
   */
  uploadCv: (profileUid: string, file: File, opts: { label?: string; cvUid?: string; syncProfile?: 'fill' | 'replace' | 'none' } = {}) => {
    const form = new FormData();
    form.append('file', file);
    if (opts.label) form.append('label', opts.label);
    if (opts.cvUid) form.append('cvUid', opts.cvUid);
    if (opts.syncProfile) form.append('syncProfile', opts.syncProfile);
    return d(api.post<{ cv: JhCv; extractedChars: number; profileSync: JhCvSync | null }>(`${BASE}/profiles/${id(profileUid)}/cvs`, form, LONG));
  },
  pasteCv: (profileUid: string, body: { label?: string; text: string; syncProfile?: 'fill' | 'replace' | 'none' }) =>
    d(api.post<JhCv & { profileSync: JhCvSync | null }>(`${BASE}/profiles/${id(profileUid)}/cvs/text`, body, LONG)),
  syncProfileFromCv: (cvUid: string, mode: 'fill' | 'replace') => d(api.post<JhCvSync>(`${BASE}/cvs/${id(cvUid)}/sync-profile`, { mode }, LONG)),
  dismissCvSync: (profileUid: string) => d(api.post<{ ok: boolean }>(`${BASE}/profiles/${id(profileUid)}/cv-sync/dismiss`, {})),
  uploadPhoto: (profileUid: string, file: File) => {
    const form = new FormData();
    form.append('file', file);
    return d(api.post<{ photo: NonNullable<JhProfile['photo']> }>(`${BASE}/profiles/${id(profileUid)}/photo`, form, LONG));
  },
  removePhoto: (profileUid: string) => d(api.delete<{ ok: boolean }>(`${BASE}/profiles/${id(profileUid)}/photo`)),
  cv: (cvUid: string) => d(api.get<JhCv>(`${BASE}/cvs/${id(cvUid)}`)),
  updateCv: (cvUid: string, body: { label?: string; text?: string; isDefault?: boolean }) => d(api.patch<JhCv>(`${BASE}/cvs/${id(cvUid)}`, body)),
  deleteCv: (cvUid: string) => d(api.delete<{ ok: boolean; fileDeleted: boolean }>(`${BASE}/cvs/${id(cvUid)}`)),
  /** AI CV review — `provider`/`model` pick the model for this run (else the candidate's / workspace default). */
  analyzeCv: (cvUid: string, choice: { provider?: string; model?: string } = {}) =>
    d(api.post<JhCvHealth>(`${BASE}/cvs/${id(cvUid)}/analyze`, choice, { timeout: 180_000 })),

  // Sponsor registers (public data shared by all workspaces)
  registers: () => d(api.get<JhRegisterStatus>(`${BASE}/registers`)),
  importRegisters: (force = false) => d(api.post<{ started: boolean }>(`${BASE}/registers/import`, { force })),

  overview: (profileUid: string) => d(api.get<JhOverview>(`${BASE}/overview`, { params: { profileUid } })),

  applications: (params: Record<string, string | number | undefined>) =>
    d(api.get<JhPage<JhApplication> & { counts: Partial<Record<JhStatus, number>> }>(`${BASE}/applications`, { params })),
  application: (uid: string) => d(api.get<JhApplication>(`${BASE}/applications/${id(uid)}`)),
  decide: (uid: string, action: 'apply' | 'skip' | 'save' | 'unsave', reason?: string) =>
    d(api.post<JhApplication>(`${BASE}/applications/${id(uid)}/decision`, { action, reason }, LONG)),
  redraft: (uid: string) => d(api.post<JhApplication>(`${BASE}/applications/${id(uid)}/draft`, {}, LONG)),
  updateApplication: (uid: string, body: Partial<Pick<JhApplication, 'status' | 'notes' | 'externalRef'>> & { coverNote?: string; answers?: JhDraft['answers']; note?: string }) =>
    d(api.patch<JhApplication>(`${BASE}/applications/${id(uid)}`, body)),
  answer: (uid: string, body: { question: string; answer: string; remember: boolean }) =>
    d(api.post<JhApplication>(`${BASE}/applications/${id(uid)}/answer`, body)),
  feedback: (uid: string, body: { reason: string; note?: string }) =>
    d(api.post<JhApplication>(`${BASE}/applications/${id(uid)}/feedback`, body)),

  jobs: (params: Record<string, string | number | undefined>) =>
    d(api.get<JhPage<JhJob> & { outcomes: Partial<Record<JhOutcome, number>> }>(`${BASE}/jobs`, { params })),
  job: (uid: string) => d(api.get<{ job: JhJob; employer: JhEmployer | null; applications: Array<Pick<JhApplication, 'applicationUid' | 'ref' | 'status' | 'profileUid' | 'agentUid' | 'evaluation'> & { profileName?: string }> }>(`${BASE}/jobs/${id(uid)}`)),
  verifyJob: (uid: string) => d(api.post<{ isOpen: boolean; checked: boolean }>(`${BASE}/jobs/${id(uid)}/verify`, {}, LONG)),
  scoreJob: (uid: string, profileUid: string, agentUid?: string) => d(api.post<{ application: JhApplication | null }>(`${BASE}/jobs/${id(uid)}/score`, { profileUid, agentUid }, LONG)),
  importJob: (url: string, profileUid?: string, agentUid?: string) =>
    d(api.post<{ job: JhJob; application: JhApplication | null; scoreError?: string }>(`${BASE}/jobs/import`, { url, profileUid, agentUid }, LONG)),

  employers: (params: Record<string, string | number | undefined>) =>
    d(api.get<JhPage<JhEmployer> & { counts: Record<string, number> }>(`${BASE}/employers`, { params })),
  employer: (uid: string) => d(api.get<{ employer: JhEmployer; sources: JhSource[]; jobs: JhJob[]; applications: Array<Pick<JhApplication, 'applicationUid' | 'ref' | 'status' | 'jobUid' | 'profileUid' | 'evaluation' | 'createdAt'>> }>(`${BASE}/employers/${id(uid)}`)),
  createEmployer: (body: { name: string; careersUrl?: string; domain?: string; country?: string; notes?: string; evidence?: string }) =>
    d(api.post<{ employer: JhEmployer; source: JhSource | null; atsDetected: boolean }>(`${BASE}/employers`, body)),
  updateEmployer: (uid: string, body: Partial<Pick<JhEmployer, 'status' | 'notes' | 'isAgency' | 'careersUrl'>> & { evidence?: string }) =>
    d(api.patch<JhEmployer>(`${BASE}/employers/${id(uid)}`, body)),
  discoverAts: (uid: string) => d(api.post<{ found: boolean; type?: string; slug?: string; jobs?: number; message?: string }>(`${BASE}/employers/${id(uid)}/discover-ats`, {}, LONG)),

  sources: () => d(api.get<{ rows: JhSource[]; heat: Partial<Record<JhHeat, number>>; catalog: JhCatalogEntry[] }>(`${BASE}/sources`)),
  createSource: (body: { url?: string; type?: JhSourceType; slug?: string; employerName?: string; region?: string; config?: Record<string, unknown> }) =>
    d(api.post<{ source: JhSource; openJobs: number | null }>(`${BASE}/sources`, body, LONG)),
  seedSources: (regions?: string[]) => d(api.post<{ created: number; total: number }>(`${BASE}/sources/seed`, { regions }, LONG)),
  updateSource: (uid: string, body: { enabled?: boolean; name?: string; pollIntervalMin?: number | null; config?: Record<string, unknown>; revive?: boolean }) =>
    d(api.patch<JhSource>(`${BASE}/sources/${id(uid)}`, body)),
  deleteSource: (uid: string) => d(api.delete<{ ok: boolean }>(`${BASE}/sources/${id(uid)}`)),
  pollSource: (uid: string) =>
    d(api.post<{ ok: true; fetched: number; created: number; closed: number; hits: number } | { ok: false; error: string }>(`${BASE}/sources/${id(uid)}/poll`, {}, LONG)),

  runs: (limit = 30) => d(api.get<{ running: boolean; runs: JhRun[] }>(`${BASE}/runs`, { params: { limit } })),
  /** All active agents, or only `agentUids` (paused ones too). */
  runNow: (force = false, agentUids?: string[]) => d(api.post<{ runUid: string }>(`${BASE}/runs`, { force, agentUids })),
};

/** The shared client already toasted this error. */
export const alreadyToasted = (e: unknown) => !!(e as { isAxiosError?: boolean } | undefined)?.isAxiosError;
